import { useState, useEffect, useRef, type ComponentType, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Plus, Layers, BarChart3, History, Award, Settings, LogOut, User, BookOpen, Zap, PlayCircle, Activity } from 'lucide-react';
import { TOKENS, FONTS } from '../ui/tokens';
import { api, clearAuthToken } from '../api/client';
import type { LucideProps } from 'lucide-react';

// ── Reuse same nav structure as Drawer ──────────────────────
interface NavItem { key: string; label: string; icon: ComponentType<LucideProps> }

const TRAINING: NavItem[] = [
  { key: '/', label: 'Dashboard', icon: Home },
  { key: '/session-log', label: 'Log Training', icon: Plus },
  { key: '/sessions', label: 'History', icon: History },
  { key: '/goals', label: 'Goals', icon: BookOpen },
  { key: '/partners', label: 'Partners', icon: User },
];

const INSIGHTS: NavItem[] = [
  { key: '/stats', label: 'Stats', icon: BarChart3 },
  { key: '/pulse', label: 'Pulse', icon: Zap },
  { key: '/cloze', label: 'Review', icon: BookOpen },
  { key: '/techniques', label: 'Techniques', icon: Layers },
  { key: '/instructionals', label: 'Instructionals', icon: PlayCircle },
  { key: '/health', label: 'Health', icon: Activity },
];

const SYSTEM: NavItem[] = [
  { key: '/achievements', label: 'Achievements', icon: Award },
  { key: '/settings', label: 'Settings', icon: Settings },
];

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      padding: '14px 14px 4px',
      fontSize: 10,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      color: TOKENS.textTertiary,
      fontFamily: FONTS.mono,
      userSelect: 'none',
    }}>{children}</div>
  );
}

/**
 * NavLink — uses React state for hover so there's no stale
 * DOM style left behind after route changes (the old direct-
 * DOM onMouseEnter/OnMouseLeave pattern caused cumulative
 * highlighting because React couldn't track the mutation).
 */
function NavLink({ item, active, onNavigate }: { item: NavItem; active: boolean; onNavigate: (key: string) => void }) {
  const [hovered, setHovered] = useState(false);
  const fill = active || hovered;

  return (
    <button
      type="button"
      onClick={() => onNavigate(item.key)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '9px 14px',
        borderRadius: 8,
        border: 'none',
        background: fill ? TOKENS.field : 'none',
        cursor: 'pointer',
        color: active ? TOKENS.amber : TOKENS.textPrimary,
        fontFamily: FONTS.body,
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        textAlign: 'left',
        transition: 'background-color 120ms ease, color 120ms ease',
      }}
    >
      <item.icon size={17} color={active ? TOKENS.amber : TOKENS.textSecondary} strokeWidth={2} />
      <span>{item.label}</span>
    </button>
  );
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef<HTMLElement>(null);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    api.getUser().then(u => setUserName(u.name ?? '')).catch(() => {});
  }, []);

  // Exact match for root, prefix+slash for nested routes.
  // prevents "/" from highlighting on every page and
  // prevents "/sessions" from matching "/session-log".
  const isActive = (key: string) =>
    key === '/'
      ? location.pathname === '/'
      : location.pathname === key || location.pathname.startsWith(key + '/');

  const handleSignOut = () => {
    clearAuthToken();
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    navigate('/');
  };

  const initial = (userName || '?').charAt(0).toUpperCase();

  return (
    <aside
      ref={sidebarRef}
      style={{
        width: 220,
        minWidth: 220,
        height: '100%',
        background: TOKENS.surface,
        borderRight: `1px solid ${TOKENS.border}`,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Profile section — mirrors v1 sidebar-user-btn */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '14px 14px',
        borderBottom: `1px solid ${TOKENS.border}`,
      }}>
        <button
          type="button"
          aria-label="Settings"
          title="Settings"
          onClick={() => navigate('/settings')}
          style={{
            width: 34, height: 34, borderRadius: 999,
            background: TOKENS.amber, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            fontFamily: FONTS.mono, fontSize: 13, fontWeight: 700,
            color: '#09090b', textTransform: 'uppercase',
          }}
        >
          {initial}
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: TOKENS.textPrimary, fontFamily: FONTS.body }}>
            {userName || 'Your Account'}
          </span>
          <span style={{ fontSize: 10, color: TOKENS.textTertiary, fontFamily: FONTS.body }}>Profile &amp; settings</span>
        </div>
      </div>

      {/* Nav groups */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <SectionLabel>Training</SectionLabel>
        {TRAINING.map(item => (
          <NavLink key={item.key} item={item} active={isActive(item.key)} onNavigate={navigate} />
        ))}
        <SectionLabel>Insights</SectionLabel>
        {INSIGHTS.map(item => (
          <NavLink key={item.key} item={item} active={isActive(item.key)} onNavigate={navigate} />
        ))}
        <SectionLabel>System</SectionLabel>
        {SYSTEM.map(item => (
          <NavLink key={item.key} item={item} active={isActive(item.key)} onNavigate={navigate} />
        ))}
      </div>

      {/* Sign out */}
      <div style={{ borderTop: `1px solid ${TOKENS.border}`, padding: 8 }}>
        <button
          type="button"
          onClick={handleSignOut}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: '9px 14px', borderRadius: 8,
            border: 'none', background: 'none', cursor: 'pointer',
            color: TOKENS.danger, fontFamily: FONTS.body,
            fontSize: 13, fontWeight: 500, textAlign: 'left',
            transition: 'background-color 120ms ease',
          }}
        >
          <LogOut size={17} color={TOKENS.danger} strokeWidth={2} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
